import { EventHandler, detach, formatUnit, Browser, closest } from '@syncfusion/ej2-base';
import { Column } from '../models/column';
import { ColumnWidthService } from '../services/width-controller';
import * as events from '../base/constant';
import { getScrollBarWidth, parentsUntil, Global, frozenDirection, isChildColumn, applyStickyLeftRightPosition, groupCaptionRowLeftRightPos, addStickyColumnPosition } from '../base/util';
import { isNullOrUndefined, addClass, removeClass } from '@syncfusion/ej2-base';
import * as literals from '../base/string-literals';
export const resizeClassList = {
    root: 'e-rhandler',
    suppress: 'e-rsuppress',
    icon: 'e-ricon',
    helper: 'e-rhelper',
    header: 'th.e-headercell',
    cursor: 'e-rcursor'
};
/**
 * `Resize` module is used to handle Resize to fit for columns.
 *
 * @hidden
 * @private
 */
export class Resize {
    /**
     * Constructor for the Grid resize module
     *
     * @param {IGrid} parent - specifies the IGrid
     * @hidden
     */
    constructor(parent) {
        this.tapped = false;
        this.isDblClk = true;
        /** @hidden */
        this.resizeProcess = false;
        this.isCancelAutoFit = false;
        this.parent = parent;
        if (this.parent.isDestroyed) {
            return;
        }
        this.widthService = new ColumnWidthService(parent);
        this.addEventListener();
    }
    /**
     * Resize by field names.
     *
     * @param  {string|string[]} fName - Defines the field name.
     * @param  {number} startRowIndex - Specifies the start row index.
     * @param  {number} endRowIndex - Specifies the end row index.
     * @returns {void}
     */
    autoFitColumns(fName, startRowIndex, endRowIndex) {
        const columnName = (fName === undefined || fName === null || fName.length <= 0) ?
            this.parent.getColumns().map((x) => { x.autoFit = true; return x.field; }) : (typeof fName === 'string') ? [fName] : fName;
        this.parent.isAutoFitColumns = true;
        if (!isNullOrUndefined(fName) && typeof fName === 'object' && fName.length !== 0) {
            fName.forEach((field) => {
                if (this.parent.getColumnByField(field)) {
                    this.parent.getColumnByField(field).autoFit = true;
                }
            });
        }
        else if (typeof fName === 'string' && fName.trim() !== '') {
            this.parent.getColumnByField(fName).autoFit = true;
        }
        if (this.parent.enableAdaptiveUI) {
            this.parent.element.classList.add('e-grid-autofit');
        }
        this.findColumn(columnName, startRowIndex, endRowIndex);
    }
    autoFit() {
        const cols = this.parent.getColumns();
        let isMaxWidthCount = 0;
        const newarray = cols.filter((c) => {
            if (!isNullOrUndefined(c.maxWidth)) {
                isMaxWidthCount++;
            }
            return c.autoFit === true;
        }).map((c) => c.field || c.headerText);
        if (newarray.length > 0 && !this.parent.preventAutoFit) {
            this.autoFitColumns(newarray);
        }
        const contentTable = this.parent.getContentTable();
        if (this.parent.allowResizing && isMaxWidthCount && (this.parent.resizeSettings.mode === 'Auto' ||
            (this.parent.resizeSettings.mode === 'Normal' && !this.parent.autoFit && newarray.length === 0))) {
            this.widthService.setWidthToTable(contentTable.style.width.indexOf('px') === -1);
        }
        else if (this.parent.autoFit && this.parent.resizeSettings.mode === 'Auto') {
            this.widthService.setWidthToTable();
        }
        if (contentTable.style.width.indexOf('px') !== -1
            && contentTable.getBoundingClientRect().width < contentTable.parentElement.clientWidth) {
            addClass([this.parent.getHeaderTable(), contentTable], ['e-tableborder']);
        }
    }
    getCellElementsByColumnIndex(columnIndex) {
        if (this.parent.frozenRows) {
            return [].slice.call(this.parent.getHeaderTable().querySelectorAll(`td.e-rowcell:nth-child(${columnIndex + 1}):not(.e-groupcaption):not(.e-detailcell)`)).concat([].slice.call(this.parent.getContentTable().querySelectorAll(`td.e-rowcell:nth-child(${columnIndex + 1}):not(.e-groupcaption):not(.e-detailcell)`)));
        }
        else {
            return [].slice.call(this.parent.getContentTable().querySelectorAll(`td.e-rowcell:nth-child(${columnIndex + 1}):not(.e-groupcaption):not(.e-detailcell)`));
        }
    }
    resizeColumn(fName, index, id, startRowIndex, endRowIndex) {
        const gObj = this.parent;
        let tWidth = 0;
        const headerTable = gObj.getHeaderTable();
        const contentTable = gObj.getContentTable();
        let footerTable;
        const headerDivTag = 'e-gridheader';
        const contentDivTag = literals.gridContent;
        const footerDivTag = literals.gridFooter;
        let indentWidth = 0;
        const uid = id ? id : this.parent.getUidByColumnField(fName);
        const columnIndex = this.parent.getNormalizedColumnIndex(uid);
        const headerTextClone = (closest(headerTable.querySelector('[data-mappinguid="' + uid + '"]'), 'th')).cloneNode(true);
        const contentTextClone = this.getCellElementsByColumnIndex(columnIndex);
        let footerTextClone;
        const columnIndexByField = this.parent.getColumnIndexByField(fName);
        if (!isNullOrUndefined(gObj.getFooterContent())) {
            footerTable = gObj.getFooterContentTable();
        }
        if (footerTable) {
            footerTextClone = footerTable.querySelectorAll(`td:nth-child(${columnIndex + 1}):not(.e-groupcaption)`);
        }
        const indentWidthClone = [].slice.call(headerTable.querySelector('tr').getElementsByClassName('e-grouptopleftcell'));
        if (indentWidthClone.length > 0) {
            for (let i = 0; i < indentWidthClone.length; i++) {
                indentWidth += indentWidthClone[parseInt(i.toString(), 10)].offsetWidth;
            }
        }
        const detailsElement = contentTable.querySelector('.e-detailrowcollapse') ||
            contentTable.querySelector('.e-detailrowexpand');
        if ((this.parent.detailTemplate || this.parent.childGrid) && detailsElement) {
            indentWidth += detailsElement.offsetWidth;
        }
        const headerText = [headerTextClone];
        const contentText = [];
        const footerText = [];
        if (footerTable) {
            for (let i = 0; i < footerTextClone.length; i++) {
                footerText[parseInt(i.toString(), 10)] = footerTextClone[parseInt(i.toString(), 10)].cloneNode(true);
            }
        }
        for (let i = 0; i < contentTextClone.length; i++) {
            contentText[parseInt(i.toString(), 10)] = contentTextClone[parseInt(i.toString(), 10)].cloneNode(true);
        }
        const wHeader = this.createTable(headerTable, headerText, headerDivTag);
        let wFooter = null;
        let wContent = null;
        if (gObj.getCurrentViewRecords().length) {
            wContent = this.createTable(contentTable, contentText, contentDivTag, startRowIndex, endRowIndex);
        }
        if (footerText.length) {
            wFooter = this.createTable(footerTable, footerText, footerDivTag);
        }
        const columnbyindex = gObj.getColumns()[parseInt(columnIndexByField.toString(), 10)];
        const width = columnbyindex.width = formatUnit(Math.max(wHeader, wContent, wFooter));
        const colMaxWidth = columnbyindex.maxWidth && parseFloat(columnbyindex.maxWidth.toString());
        if (parseInt(width, 10) > colMaxWidth) {
            columnbyindex.width = colMaxWidth;
        }
        this.widthService.setColumnWidth(gObj.getColumns()[parseInt(columnIndexByField.toString(), 10)]);
        const result = gObj.getColumns().some((x) => (x.visible || gObj.groupSettings.columns.length) &&
            (x.width === null || x.width === undefined || x.width.length <= 0));
        if (result === false) {
            const element = gObj.getColumns();
            for (let i = 0; i < element.length; i++) {
                if (element[parseInt(i.toString(), 10)].visible) {
                    tWidth = tWidth + parseFloat(element[parseInt(i.toString(), 10)].width);
                }
            }
        }
        let calcTableWidth = tWidth + indentWidth;
        if (tWidth > 0) {
            if (this.parent.detailTemplate || this.parent.childGrid) {
                this.widthService.setColumnWidth(new Column({ width: '30px' }));
            }
            if (this.parent.resizeSettings.mode === 'Auto') {
                calcTableWidth = '100%';
            }
            headerTable.style.width = formatUnit(calcTableWidth);
            contentTable.style.width = formatUnit(calcTableWidth);
            if (!isNullOrUndefined(footerTable)) {
                footerTable.style.width = formatUnit(calcTableWidth);
            }
        }
        if (gObj.isFrozenGrid() && gObj.enableColumnVirtualization) {
            this.widthService.refreshFrozenScrollbar();
        }
        const tableWidth = headerTable.offsetWidth;
        const contentwidth = contentTable.parentElement.scrollWidth;
        if (contentwidth > tableWidth) {
            if (!isNullOrUndefined(contentTable.querySelector('.e-emptyrow'))) {
                addClass([headerTable], ['e-tableborder']);
                removeClass([contentTable], ['e-tableborder']);
            }
            else {
                addClass([headerTable, contentTable], ['e-tableborder']);
            }
            removeClass([gObj.element], ['e-left-shadow', 'e-right-shadow']);
        }
        else {
            removeClass([headerTable, contentTable], ['e-tableborder']);
            if (gObj.getVisibleFrozenRightCount()) {
                addClass([gObj.element], 'e-right-shadow');
            }
        }
        if (!isNullOrUndefined(footerTable)) {
            footerTable.classList.add('e-tableborder');
        }
    }
    /**
     * To destroy the resize
     *
     * @returns {void}
     * @hidden
     */
    destroy() {
        const gridElement = this.parent.element;
        if (!gridElement || (!gridElement.querySelector('.' + literals.gridHeader) && !gridElement.querySelector('.' + literals.gridContent))) {
            return;
        }
        this.widthService = null;
        this.unwireEvents();
        this.removeEventListener();
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {string} returns the module name
     * @private
     */
    getModuleName() {
        return 'resize';
    }
    findColumn(fName, startRowIndex, endRowIndex) {
        for (let i = 0; i < fName.length; i++) {
            const fieldName = fName[parseInt(i.toString(), 10)];
            const columnIndex = this.parent.getColumnIndexByField(fieldName);
            const column = this.parent.getColumns()[parseInt(columnIndex.toString(), 10)];
            if (columnIndex > -1 && !isNullOrUndefined(column) && column.visible === true) {
                this.resizeColumn(fieldName, columnIndex, null, startRowIndex, endRowIndex);
            }
        }
        if (this.parent.allowTextWrap) {
            this.parent.notify(events.freezeRender, { case: 'refreshHeight', isModeChg: true });
        }
        if (this.parent.isFrozenGrid()) {
            this.refreshResizefrzCols(true, true);
        }
    }
    /**
     * To create table for autofit
     *
     * @param {Element} table - specifies the table
     * @param {Element[]} text - specifies the text
     * @param {string} tag - specifies the tag name
     * @param  {number} startRowIndex - Specifies the start row index.
     * @param  {number} endRowIndex - Specifies the end row index.
     * @returns {number} returns the number
     * @hidden
     */
    createTable(table, text, tag, startRowIndex = 1, endRowIndex = text.length) {
        if (startRowIndex > endRowIndex) {
            startRowIndex ^= endRowIndex;
            endRowIndex ^= startRowIndex;
            startRowIndex ^= endRowIndex;
        }
        const myTableDiv = this.parent.createElement('div');
        const adaptiveClass = this.parent.enableAdaptiveUI ? ' e-bigger' : '';
        myTableDiv.className = this.parent.element.className + adaptiveClass;
        myTableDiv.style.cssText = 'display: inline-block;visibility:hidden;position:absolute';
        const mySubDiv = this.parent.createElement('div');
        mySubDiv.className = tag;
        const myTable = this.parent.createElement('table', { attrs: { role: 'grid' } });
        myTable.className = table.className;
        myTable.classList.add('e-resizetable');
        myTable.style.cssText = 'table-layout: auto;width: auto';
        let thead;
        if (tag === 'e-gridheader') {
            thead = this.parent.createElement('thead');
            myTable.appendChild(thead);
        }
        const myTr = this.parent.createElement('tr');
        for (let i = (startRowIndex <= 0 ? 1 : startRowIndex); i <= (endRowIndex > text.length ? text.length : endRowIndex); i++) {
            const tr = myTr.cloneNode();
            tr.className = table.querySelector('tr').className;
            tr.appendChild(text[parseInt((i - 1).toString(), 10)]);
            if (!isNullOrUndefined(thead)) {
                thead.appendChild(tr);
            }
            else {
                myTable.appendChild(tr);
            }
        }
        mySubDiv.appendChild(myTable);
        myTableDiv.appendChild(mySubDiv);
        document.body.appendChild(myTableDiv);
        const offsetWidthValue = myTable.getBoundingClientRect().width;
        document.body.removeChild(myTableDiv);
        return Math.ceil(offsetWidthValue);
    }
    /**
     * @returns {void}
     * @hidden
     */
    addEventListener() {
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.on(events.headerRefreshed, this.refreshHeight, this);
        this.parent.on(events.refreshResizePosition, this.refreshResizePosition, this);
        this.parent.on(events.initialEnd, this.wireEvents, this);
        this.parent.on(events.contentReady, this.autoFit, this);
        this.parent.on(events.refreshHandlers, this.refreshHeight, this);
        this.parent.on(events.destroy, this.destroy, this);
    }
    /**
     * @returns {void}
     * @hidden
     */
    removeEventListener() {
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.off(events.headerRefreshed, this.refreshHeight);
        this.parent.off(events.refreshResizePosition, this.refreshResizePosition);
        this.parent.off(events.initialEnd, this.wireEvents);
        this.parent.off(events.refreshHandlers, this.refreshHeight);
        this.parent.off(events.destroy, this.destroy);
    }
    /**
     * @returns {void}
     * @hidden
     */
    render() {
        this.unwireEvents();
        this.wireEvents();
        this.setHandlerHeight();
    }
    refreshHeight() {
        if (this.parent.getHeaderTable()) {
            const element = this.getResizeHandlers();
            for (let i = 0; i < element.length; i++) {
                if (element[parseInt(i.toString(), 10)].parentElement.offsetHeight > 0) {
                    element[parseInt(i.toString(), 10)].style.height = '100%';
                }
            }
            this.setHandlerHeight();
        }
    }
    wireEvents() {
        EventHandler.add(this.parent.getHeaderContent(), Browser.touchStartEvent, this.touchResizeStart, this);
        EventHandler.add(this.parent.getHeaderContent(), events.dblclick, this.callAutoFit, this);
    }
    unwireEvents() {
        EventHandler.remove(this.parent.getHeaderContent(), Browser.touchStartEvent, this.touchResizeStart);
        EventHandler.remove(this.parent.getHeaderContent(), events.dblclick, this.callAutoFit);
    }
    getResizeHandlers() {
        return [].slice.call(this.parent.getHeaderTable().getElementsByClassName(resizeClassList.root));
    }
    setHandlerHeight() {
        const element = [].slice.call(this.parent.getHeaderTable().getElementsByClassName(resizeClassList.suppress));
        for (let i = 0; i < element.length; i++) {
            element[parseInt(i.toString(), 10)].style.height = '100%';
        }
    }
    callAutoFit(e) {
        if (Browser.isDevice && typeof e.preventDefault === 'function' && this.isDblClk) {
            e.preventDefault();
        }
        if (e.target.classList.contains('e-rhandler') && !this.isCancelAutoFit) {
            const col = this.getTargetColumn(e);
            if (col.columns) {
                return;
            }
            this.resizeColumn(col.field, this.parent.getNormalizedColumnIndex(col.uid), col.uid);
            if (this.parent.isFrozenGrid()) {
                this.refreshResizefrzCols(true, true);
            }
            if (this.isDblClk && e.type === 'mousedown') {
                const args = { e: e, column: col };
                this.parent.trigger(events.resizeStop, args);
            }
            const header = closest(e.target, resizeClassList.header);
            header.classList.add('e-resized');
        }
    }
    touchResizeStart(e) {
        if (!Global.timer) {
            Global.timer = setTimeout(() => {
                Global.timer = null;
            }, 300);
            return this.resizeStart(e);
        }
        else {
            clearTimeout(Global.timer);
            Global.timer = null;
            this.callAutoFit(e);
        }
    }
    resizeStart(e) {
        const contentWidth = this.parent.getContentTable().style.width;
        let isWidthOver100 = false;
        if (contentWidth.indexOf('%') !== -1 && parseFloat(contentWidth) > 100) {
            isWidthOver100 = true;
        }
        if (e.target.classList.contains('e-rhandler')) {
            this.isCancelAutoFit = false;
            const args = { e: e, column: this.getTargetColumn(e) };
            this.parent.trigger(events.resizeStart, args, (args) => {
                if (args.cancel || this.parent.isEdit) {
                    this.cancelResizeAction();
                    this.isCancelAutoFit = true;
                    return;
                }
            });
            if (!this.isCancelAutoFit) {
                if (!this.helper) {
                    if (this.getScrollBarWidth() === 0 || isWidthOver100) {
                        this.resizeProcess = true;
                        for (const col of this.refreshColumnWidth()) {
                            this.widthService.setColumnWidth(col);
                        }
                        if (this.parent.allowGrouping) {
                            for (let i = 0; i < this.parent.groupSettings.columns.length; i++) {
                                this.widthService.setColumnWidth(new Column({ width: '30px' }), i);
                            }
                        }
                        if (this.parent.isRowDragable()) {
                            this.widthService.setColumnWidth(new Column({ width: '30px' }));
                        }
                        this.widthService.setWidthToTable();
                        this.resizeProcess = false;
                    }
                    this.refreshStackedColumnWidth();
                    this.element = e.target;
                    this.parentElementWidth = this.parent.element.getBoundingClientRect().width;
                    this.appendHelper();
                    this.column = this.getTargetColumn(e);
                    this.pageX = this.getPointX(e);
                    if (this.column.getFreezeTableName() === literals.frozenRight) {
                        if (this.parent.enableRtl) {
                            this.minMove = (this.column.minWidth ? parseFloat(this.column.minWidth.toString()) : 0)
                                - parseFloat(isNullOrUndefined(this.column.width) ? '' : this.column.width.toString());
                        }
                        else {
                            this.minMove = parseFloat(isNullOrUndefined(this.column.width) ? '' : this.column.width.toString())
                                - (this.column.minWidth ? parseFloat(this.column.minWidth.toString()) : 0);
                        }
                    }
                    else if (this.parent.enableRtl) {
                        this.minMove = parseFloat(this.column.width.toString())
                            - (this.column.minWidth ? parseFloat(this.column.minWidth.toString()) : 0);
                    }
                    else {
                        this.minMove = (this.column.minWidth ? parseFloat(this.column.minWidth.toString()) : 0)
                            - parseFloat(isNullOrUndefined(this.column.width) ? '' : this.column.width.toString());
                    }
                    this.minMove += this.pageX;
                }
                EventHandler.add(document, Browser.touchEndEvent, this.resizeEnd, this);
                EventHandler.add(this.parent.element, Browser.touchMoveEvent, this.resizing, this);
                if (Browser.isDevice && !this.helper.classList.contains(resizeClassList.icon)) {
                    this.helper.classList.add(resizeClassList.icon);
                    EventHandler.add(document, Browser.touchStartEvent, this.removeHelper, this);
                    EventHandler.add(this.helper, Browser.touchStartEvent, this.resizeStart, this);
                }
                else {
                    this.updateCursor('add');
                }
            }
        }
    }
    cancelResizeAction(removeEvents) {
        if (removeEvents) {
            EventHandler.remove(this.parent.element, Browser.touchMoveEvent, this.resizing);
            EventHandler.remove(document, Browser.touchEndEvent, this.resizeEnd);
            this.updateCursor('remove');
        }
        if (Browser.isDevice && !isNullOrUndefined(this.helper)) {
            EventHandler.remove(document, Browser.touchStartEvent, this.removeHelper);
            EventHandler.remove(this.helper, Browser.touchStartEvent, this.resizeStart);
        }
        if (!isNullOrUndefined(this.helper)) {
            detach(this.helper);
        }
        this.refresh();
    }
    getWidth(width, minWidth, maxWidth) {
        if (minWidth && width < minWidth) {
            return minWidth;
        }
        else if ((maxWidth && width > maxWidth)) {
            return maxWidth;
        }
        else {
            return width;
        }
    }
    updateResizeEleHeight() {
        const elements = [].slice.call(this.parent.getHeaderContent().getElementsByClassName('e-rhandler'));
        for (let i = 0; i < elements.length; i++) {
            elements[parseInt(i.toString(), 10)].style.height = this.element.parentElement.offsetHeight + 'px';
        }
    }
    getColData(column, mousemove) {
        return {
            width: parseFloat(isNullOrUndefined(this.widthService.getWidth(column)) || this.widthService.getWidth(column) === 'auto' ? '0'
                : this.widthService.getWidth(column).toString()) + mousemove,
            minWidth: column.minWidth ? parseFloat(column.minWidth.toString()) : null,
            maxWidth: column.maxWidth ? parseFloat(column.maxWidth.toString()) : null
        };
    }
    refreshResizeFixedCols(pos) {
        const cols = this.parent.getColumns();
        const translateX = this.parent.enableColumnVirtualization ? this.parent.translateX : 0;
        const th = [].slice.call(this.parent.getHeaderContent().querySelector('tbody').querySelectorAll('.e-fixedfreeze')).concat([].slice.call(this.parent.getContent().querySelectorAll('.e-fixedfreeze')));
        for (let i = 0; i < th.length; i++) {
            const node = th[parseInt(i.toString(), 10)];
            let column;
            if (node.classList.contains('e-summarycell')) {
                const uid = node.getAttribute('data-mappinguid');
                column = this.parent.getColumnByUid(uid);
            }
            else {
                const index = parseInt(node.getAttribute('aria-colindex'), 10) - 1;
                column = cols[parseInt(index.toString(), 10)];
            }
            let width = 0;
            if (pos === 'Left') {
                if (this.parent.getVisibleFrozenLeftCount()) {
                    width = this.parent.getIndentCount() * 30;
                }
                else if (this.parent.getFrozenMode() === 'Right') {
                    width = this.parent.groupSettings.columns.length * 30;
                }
                for (let j = 0; j < cols.length; j++) {
                    if (column.index > cols[parseInt(j.toString(), 10)].index) {
                        if (column.uid === cols[parseInt(j.toString(), 10)].uid) {
                            break;
                        }
                        if ((cols[parseInt(j.toString(), 10)].freeze === 'Left' || cols[parseInt(j.toString(), 10)].isFrozen) ||
                            cols[parseInt(j.toString(), 10)].freeze === 'Fixed') {
                            if (cols[parseInt(j.toString(), 10)].visible) {
                                width += parseFloat(cols[parseInt(j.toString(), 10)].width.toString());
                            }
                        }
                    }
                }
                applyStickyLeftRightPosition(node, ((width === 0 ? width : width - 1) - translateX), this.parent.enableRtl, 'Left');
            }
            if (pos === 'Right') {
                width = this.parent.getFrozenMode() === 'Right' && this.parent.isRowDragable() ? 30 : 0;
                for (let j = cols.length - 1; j >= 0; j--) {
                    if (column.uid === cols[parseInt(j.toString(), 10)].uid) {
                        break;
                    }
                    if (cols[parseInt(j.toString(), 10)].freeze === 'Right' || cols[parseInt(j.toString(), 10)].freeze === 'Fixed') {
                        if (cols[parseInt(j.toString(), 10)].visible) {
                            width += parseFloat(cols[parseInt(j.toString(), 10)].width.toString());
                        }
                    }
                }
                let colSpanwidth = 0;
                if (node.colSpan > 1) {
                    colSpanwidth = this.calculateColspanWidth(cols, node, column.index);
                }
                applyStickyLeftRightPosition(node, (width - colSpanwidth) + translateX, this.parent.enableRtl, 'Right');
            }
        }
    }
    calculateColspanWidth(cols, node, index) {
        let width = 0;
        for (let j = index + 1; j < index + node.colSpan; j++) {
            width += parseInt(cols[parseInt(j.toString(), 10)].width.toString(), 10);
        }
        return width;
    }
    refreshResizePosition() {
        this.refreshResizefrzCols(true);
    }
    refreshResizefrzCols(freezeRefresh, isAutoFitCol) {
        const translateX = this.parent.enableColumnVirtualization ? this.parent.translateX : 0;
        if (freezeRefresh || ((this.column.freeze === 'Left' || this.column.isFrozen) ||
            (this.column.columns && frozenDirection(this.column) === 'Left'))) {
            let width = this.parent.getIndentCount() * 30;
            const columns = this.parent.getColumns().filter((col) => col.freeze === 'Left' || col.isFrozen);
            if (!freezeRefresh || isAutoFitCol) {
                this.frzHdrRefresh('Left');
            }
            for (let i = 0; i < columns.length; i++) {
                if (freezeRefresh || (columns[parseInt(i.toString(), 10)].index > this.column.index)) {
                    let elements = [];
                    if (this.parent.frozenRows) {
                        elements = [].slice.call(this.parent.getHeaderContent().querySelectorAll('td[aria-colindex="' + (i + 1) + '"]')).concat([].slice.call(this.parent.getContent().querySelectorAll('td[aria-colindex="' + (i + 1) + '"]')));
                    }
                    else {
                        elements = [].slice.call(this.parent.getContent().querySelectorAll('td[aria-colindex="' + (i + 1) + '"]'));
                    }
                    elements.filter((cell) => {
                        applyStickyLeftRightPosition(cell, width - translateX, this.parent.enableRtl, 'Left');
                    });
                    if (this.parent.enableColumnVirtualization) {
                        columns[parseInt(i.toString(), 10)].valueX = width;
                    }
                }
                if (columns[parseInt(i.toString(), 10)].visible) {
                    width += parseFloat(columns[parseInt(i.toString(), 10)].width.toString());
                }
            }
            this.refreshResizeFixedCols('Left');
        }
        if (freezeRefresh || (this.column.freeze === 'Right' || (this.column.columns && frozenDirection(this.column) === 'Right'))) {
            let width = this.parent.getFrozenMode() === 'Right' && this.parent.isRowDragable() ? 30 : 0;
            const columns = this.parent.getColumns();
            if (!freezeRefresh || isAutoFitCol) {
                this.frzHdrRefresh('Right');
            }
            const columnsRight = columns.filter((col) => col.freeze === 'Right');
            for (let i = columns.length - 1; i >= columns.length - columnsRight.length; i--) {
                let elements = [];
                if (this.parent.frozenRows) {
                    elements = [].slice.call(this.parent.getHeaderContent().querySelectorAll('td[aria-colindex="' + (i + 1) + '"]')).concat([].slice.call(this.parent.getContent().querySelectorAll('td[aria-colindex="' + (i + 1) + '"]')));
                }
                else {
                    elements = [].slice.call(this.parent.getContent().querySelectorAll('td[aria-colindex="' + (i + 1) + '"]'));
                }
                elements.filter((cell) => {
                    let colSpanwidth = 0;
                    if (cell.colSpan > 1) {
                        colSpanwidth = this.calculateColspanWidth(columns, cell, columns[parseInt(i.toString(), 10)].index);
                    }
                    applyStickyLeftRightPosition(cell, (width - colSpanwidth) + translateX, this.parent.enableRtl, 'Right');
                });
                if (this.parent.enableColumnVirtualization) {
                    columns[parseInt(i.toString(), 10)].valueX = width;
                }
                if (columns[parseInt(i.toString(), 10)].visible) {
                    width = width + parseFloat(columns[parseInt(i.toString(), 10)].width.toString());
                }
            }
            this.refreshResizeFixedCols('Right');
        }
        if (this.column && (this.column.freeze === 'Fixed' || (this.column.columns && frozenDirection(this.column) === 'Fixed'))) {
            this.refreshResizeFixedCols('Left');
            this.refreshResizeFixedCols('Right');
            this.frzHdrRefresh('Left');
            this.frzHdrRefresh('Right');
        }
        if (this.parent.groupSettings.columns.length && this.parent.aggregates.length &&
            this.parent.getContent().querySelector('.e-groupcaptionrow')) {
            this.refreshGroupCaptionRow();
        }
    }
    refreshGroupCaptionRow() {
        const capRow = [].slice.call(this.parent.getContent().querySelectorAll('.e-groupcaptionrow'));
        for (let i = 0; i < capRow.length; i++) {
            const tr = capRow[parseInt(i.toString(), 10)];
            if (tr.querySelector('.e-summarycell')) {
                groupCaptionRowLeftRightPos(tr, this.parent);
            }
        }
    }
    frzHdrRefresh(pos) {
        const translateX = this.parent.enableColumnVirtualization ? this.parent.translateX : 0;
        if (pos === 'Left') {
            const tr = [].slice.call(this.parent.getHeaderContent().querySelector('thead').querySelectorAll('tr'));
            for (let i = 0; i < tr.length; i++) {
                const th = [].slice.call(tr[parseInt(i.toString(), 10)].querySelectorAll('.e-leftfreeze,.e-fixedfreeze'));
                for (let j = 0; j < th.length; j++) {
                    const node = th[parseInt(j.toString(), 10)];
                    if (node.classList.contains('e-rowdragheader') || node.classList.contains('e-dragheadercell') ||
                        node.classList.contains('e-grouptopleftcell')) {
                        continue;
                    }
                    const column = this.getParticularCol(node);
                    const cols = this.parent.getColumns();
                    let width = 0;
                    let summarycell = [];
                    if (this.parent.aggregates.length && this.parent.getFooterContent()) {
                        if (this.parent.getContent().querySelectorAll('.e-summaryrow').length) {
                            const summaryRows = [].slice.call(this.parent.getContent().querySelectorAll('.e-summaryrow'));
                            summaryRows.filter((row) => {
                                summarycell.push(row.querySelector('[data-mappinguid="' + column.uid + '"]'));
                            });
                        }
                        summarycell = summarycell.concat([].slice.call(this.parent.getFooterContent().querySelectorAll('[data-mappinguid="' + column.uid + '"]')));
                    }
                    if (node.classList.contains('e-fixedfreeze')) {
                        if (this.parent.getVisibleFrozenLeftCount()) {
                            width = this.parent.getIndentCount() * 30;
                        }
                        else if (this.parent.getFrozenMode() === 'Right') {
                            width = this.parent.groupSettings.columns.length * 30;
                        }
                        for (let w = 0; w < cols.length; w++) {
                            if (column.index > cols[parseInt(w.toString(), 10)].index) {
                                if (column.uid === cols[parseInt(w.toString(), 10)].uid) {
                                    break;
                                }
                                if ((cols[parseInt(w.toString(), 10)].freeze === 'Left' || cols[parseInt(w.toString(), 10)].isFrozen) ||
                                    cols[parseInt(w.toString(), 10)].freeze === 'Fixed') {
                                    if (cols[parseInt(w.toString(), 10)].visible) {
                                        width += parseInt(cols[parseInt(w.toString(), 10)].width.toString(), 10);
                                    }
                                }
                            }
                        }
                        if (summarycell && summarycell.length) {
                            summarycell.filter((cell) => {
                                applyStickyLeftRightPosition(cell, width - translateX, this.parent.enableRtl, 'Left');
                            });
                        }
                        applyStickyLeftRightPosition(node, ((width === 0 ? width : width - 1) - translateX), this.parent.enableRtl, 'Left');
                    }
                    else {
                        width = this.parent.getIndentCount() * 30;
                        if (column.index === 0) {
                            if (summarycell && summarycell.length) {
                                summarycell.filter((cell) => {
                                    applyStickyLeftRightPosition(cell, width - translateX, this.parent.enableRtl, 'Left');
                                });
                            }
                            applyStickyLeftRightPosition(node, width - translateX, this.parent.enableRtl, 'Left');
                            if (this.parent.enableColumnVirtualization) {
                                column.valueX = width;
                            }
                        }
                        else {
                            for (let k = 0; k < cols.length; k++) {
                                if (column.index < cols[parseInt(k.toString(), 10)].index ||
                                    column.uid === cols[parseInt(k.toString(), 10)].uid) {
                                    break;
                                }
                                if (cols[parseInt(k.toString(), 10)].visible) {
                                    width += parseInt(cols[parseInt(k.toString(), 10)].width.toString(), 10);
                                }
                            }
                            if (summarycell && summarycell.length) {
                                summarycell.filter((cell) => {
                                    applyStickyLeftRightPosition(cell, width - translateX, this.parent.enableRtl, 'Left');
                                });
                            }
                            applyStickyLeftRightPosition(node, width - translateX, this.parent.enableRtl, 'Left');
                            if (this.parent.enableColumnVirtualization) {
                                column.valueX = width;
                            }
                        }
                        addStickyColumnPosition(this.parent, column, node);
                    }
                }
            }
        }
        if (pos === 'Right') {
            const tr = [].slice.call(this.parent.getHeaderContent().querySelector('thead').querySelectorAll('tr'));
            for (let i = 0; i < tr.length; i++) {
                const th = [].slice.call(tr[parseInt(i.toString(), 10)].querySelectorAll('.e-rightfreeze, .e-fixedfreeze'));
                for (let j = th.length - 1; j >= 0; j--) {
                    const node = th[parseInt(j.toString(), 10)];
                    const column = this.getParticularCol(node);
                    const cols = this.parent.getColumns();
                    let width = 0;
                    let summarycell = [];
                    if (this.parent.aggregates.length && this.parent.getFooterContent()) {
                        if (this.parent.getContent().querySelectorAll('.e-summaryrow').length) {
                            const summaryRows = [].slice.call(this.parent.getContent().querySelectorAll('.e-summaryrow'));
                            summaryRows.filter((row) => {
                                summarycell.push(row.querySelector('[data-mappinguid="' + column.uid + '"]'));
                            });
                        }
                        summarycell = summarycell.concat([].slice.call(this.parent.getFooterContent().querySelectorAll('[data-mappinguid="' + column.uid + '"]')));
                    }
                    if (node.classList.contains('e-fixedfreeze')) {
                        width = this.parent.getFrozenMode() === 'Right' && this.parent.isRowDragable() ? 30 : 0;
                        for (let w = cols.length - 1; w >= 0; w--) {
                            if (column.index < cols[parseInt(w.toString(), 10)].index) {
                                if ((column.columns && isChildColumn(column, cols[parseInt(w.toString(), 10)].uid)) ||
                                    column.index > cols[parseInt(w.toString(), 10)].index) {
                                    break;
                                }
                                if (cols[parseInt(w.toString(), 10)].freeze === 'Right' ||
                                    cols[parseInt(w.toString(), 10)].freeze === 'Fixed') {
                                    if (cols[parseInt(w.toString(), 10)].visible) {
                                        width += parseFloat(cols[parseInt(w.toString(), 10)].width.toString());
                                    }
                                }
                            }
                        }
                        if (summarycell.length) {
                            summarycell.filter((cell) => {
                                applyStickyLeftRightPosition(cell, width + translateX, this.parent.enableRtl, 'Right');
                            });
                        }
                        applyStickyLeftRightPosition(node, width + translateX, this.parent.enableRtl, 'Right');
                    }
                    else {
                        width = this.parent.getFrozenMode() === 'Right' && this.parent.isRowDragable() ? 30 : 0;
                        for (let k = cols.length - 1; k >= 0; k--) {
                            if ((column.columns && isChildColumn(column, cols[parseInt(k.toString(), 10)].uid)) ||
                                column.index > cols[parseInt(k.toString(), 10)].index ||
                                column.uid === cols[parseInt(k.toString(), 10)].uid) {
                                break;
                            }
                            if (cols[parseInt(k.toString(), 10)].visible) {
                                width += parseInt(cols[parseInt(k.toString(), 10)].width.toString(), 10);
                            }
                        }
                        if (summarycell.length) {
                            summarycell.filter((cell) => {
                                applyStickyLeftRightPosition(cell, width + translateX, this.parent.enableRtl, 'Right');
                            });
                        }
                        applyStickyLeftRightPosition(node, width + translateX, this.parent.enableRtl, 'Right');
                        if (this.parent.enableColumnVirtualization) {
                            column.valueX = width;
                        }
                    }
                }
            }
        }
    }
    getParticularCol(node) {
        const uid = node.classList.contains('e-filterbarcell') ? node.getAttribute('data-mappinguid') :
            node.querySelector('[data-mappinguid]').getAttribute('data-mappinguid');
        return this.parent.getColumnByUid(uid);
    }
    resizing(e) {
        if (Browser.isDevice && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        if (isNullOrUndefined(this.column)) {
            return;
        }
        let offsetWidth = 0;
        if (isNullOrUndefined(this.column)) {
            offsetWidth = parentsUntil(this.element, 'th').offsetWidth;
        }
        if (this.parent.allowTextWrap) {
            this.updateResizeEleHeight();
            this.setHelperHeight();
        }
        let pageX = this.getPointX(e);
        const mousemove = this.parent.enableRtl ? -(pageX - this.pageX) : (pageX - this.pageX);
        const prevWidth = parseFloat(this.column.width.toString());
        const colData = this.getColData(this.column, mousemove);
        if (!colData.width) {
            colData.width = closest(this.element, 'th').offsetWidth;
        }
        let width = this.getWidth(colData.width, colData.minWidth, colData.maxWidth);
        this.parent.log('resize_min_max', { column: this.column, width });
        if (((!this.parent.enableRtl && this.minMove >= pageX) || (this.parent.enableRtl && this.minMove <= pageX))) {
            width = this.column.minWidth ? parseFloat(this.column.minWidth.toString()) : 10;
            this.pageX = pageX = this.minMove;
        }
        if (width !== parseFloat(isNullOrUndefined(this.column.width) || this.column.width === 'auto' ?
            offsetWidth.toString() : this.column.width.toString())) {
            this.pageX = pageX;
            this.column.width = formatUnit(width);
            const args = {
                e: e,
                column: this.column
            };
            this.parent.trigger(events.onResize, args);
            if (args.cancel) {
                this.cancelResizeAction(true);
                return;
            }
            let columns = [this.column];
            let finalColumns = [this.column];
            if (this.column.columns) {
                columns = this.getSubColumns(this.column, []);
                columns = this.calulateColumnsWidth(columns, false, mousemove);
                finalColumns = this.calulateColumnsWidth(columns, true, mousemove);
            }
            let columnWidth = 0;
            const minMaxCols = this.parent.getColumns().filter((col) => {
                if (col.visible) {
                    columnWidth += parseInt(col.width.toString(), 10);
                }
                return col.visible && (col.maxWidth || col.minWidth);
            });
            if (this.parent.resizeSettings.mode === 'Auto' && minMaxCols.length && columnWidth < this.parentElementWidth) {
                const columns = this.parent.getColumns().filter((col) => col.visible && col.uid !== this.column.uid);
                const distributedCols = this.distributeColWidth(columns, mousemove, 0);
                if (Math.round(distributedCols.usedWidth) !== Math.abs(mousemove)) {
                    finalColumns[0].width = prevWidth + (distributedCols.usedWidth * Math.sign(mousemove));
                }
                finalColumns = [...finalColumns, ...distributedCols.columns];
            }
            this.resizeProcess = true;
            for (const col of finalColumns) {
                this.widthService.setColumnWidth(col, null, 'resize');
            }
            this.resizeProcess = false;
            this.updateHelper();
        }
        if (this.parent.isFrozenGrid()) {
            this.refreshResizefrzCols();
        }
        this.isDblClk = false;
    }
    distributeColWidth(columns, distributeWidth, usedWidth) {
        const incDecWidth = distributeWidth / columns.length;
        const absIncDecWidth = Math.abs(incDecWidth);
        const appliedColumns = [];
        const availableColumns = [];
        let extraWidth = 0;
        for (const col of columns) {
            const afterIncDec = parseFloat(col.width.toString()) - incDecWidth;
            if (col.minWidth && afterIncDec < parseInt(col.minWidth.toString(), 10)) {
                const remainWidth = parseFloat((parseInt(col.minWidth.toString(), 10) - afterIncDec).toFixed(3));
                extraWidth += remainWidth;
                usedWidth += (absIncDecWidth - remainWidth);
                col.width = col.minWidth;
                appliedColumns.push(col);
            }
            else if (col.maxWidth && afterIncDec > parseInt(col.maxWidth.toString(), 10)) {
                const remainWidth = parseFloat((afterIncDec - parseInt(col.maxWidth.toString(), 10)).toFixed(3));
                extraWidth += remainWidth;
                usedWidth += (absIncDecWidth - remainWidth);
                col.width = col.maxWidth;
                appliedColumns.push(col);
            }
            else {
                usedWidth += absIncDecWidth;
                col.width = afterIncDec;
                availableColumns.push(col);
            }
        }
        const distributedCols = extraWidth && availableColumns.length
            ? this.distributeColWidth(availableColumns, extraWidth * Math.sign(distributeWidth), usedWidth)
            : { columns: availableColumns, usedWidth };
        return { columns: [...appliedColumns, ...distributedCols.columns], usedWidth: distributedCols.usedWidth };
    }
    calulateColumnsWidth(columns, isUpdate, mousemove) {
        const finalColumns = [];
        for (const col of columns) {
            let totalWidth = 0;
            for (let i = 0; i < columns.length; i++) {
                totalWidth += parseFloat(columns[parseInt(i.toString(), 10)].width.toString());
            }
            const colData = this.getColData(col, (parseFloat(col.width)) * mousemove / totalWidth);
            const colWidth = this.getWidth(colData.width, colData.minWidth, colData.maxWidth);
            if ((colWidth !== parseFloat(col.width.toString()))) {
                if (isUpdate) {
                    col.width = formatUnit(colWidth < 1 ? 1 : colWidth);
                }
                finalColumns.push(col);
            }
        }
        return finalColumns;
    }
    getSubColumns(column, subColumns) {
        for (const col of column.columns) {
            if (col.visible !== false && col.allowResizing) {
                if (col.columns) {
                    this.getSubColumns(col, subColumns);
                }
                else {
                    subColumns.push(col);
                }
            }
        }
        return subColumns;
    }
    resizeEnd(e) {
        if (!this.helper || this.parent.isDestroyed) {
            return;
        }
        const gObj = this.parent;
        if (gObj.isFrozenGrid()) {
            this.refreshResizePosition();
        }
        EventHandler.remove(this.parent.element, Browser.touchMoveEvent, this.resizing);
        EventHandler.remove(document, Browser.touchEndEvent, this.resizeEnd);
        if (Browser.isDevice) {
            EventHandler.remove(document, Browser.touchStartEvent, this.removeHelper);
            EventHandler.remove(this.helper, Browser.touchStartEvent, this.resizeStart);
        }
        this.updateCursor('remove');
        detach(this.helper);
        const args = { e: e, column: this.column };
        const content = this.parent.getContent().querySelector('.' + literals.content);
        const cTable = content;
        if (cTable.scrollHeight > cTable.clientHeight) {
            this.parent.scrollModule.setPadding();
            cTable.style.overflowY = 'scroll';
        }
        if (!Global.timer || !this.isDblClk) {
            this.parent.trigger(events.resizeStop, args);
            closest(this.element, '.e-headercell').classList.add('e-resized');
        }
        this.isFrozenColResized = false;
        if (this.parent.allowTextWrap) {
            this.updateResizeEleHeight();
            this.parent.notify(events.textWrapRefresh, { case: 'textwrap' });
        }
        const headerTable = gObj.getHeaderTable();
        const contentTable = gObj.getContentTable();
        let footerTable;
        if (!isNullOrUndefined(gObj.getFooterContent())) {
            footerTable = gObj.getFooterContentTable();
        }
        const tableWidth = headerTable.offsetWidth;
        const contentwidth = (gObj.getContent().scrollWidth);
        if (contentwidth > tableWidth) {
            if (!isNullOrUndefined(contentTable.querySelector('.e-emptyrow'))) {
                addClass([headerTable], ['e-tableborder']);
                removeClass([contentTable], ['e-tableborder']);
            }
            else {
                addClass([headerTable, contentTable], ['e-tableborder']);
            }
            removeClass([gObj.element], ['e-left-shadow', 'e-right-shadow']);
        }
        else {
            removeClass([headerTable, contentTable], ['e-tableborder']);
            if (gObj.getVisibleFrozenRightCount()) {
                addClass([gObj.element], 'e-right-shadow');
            }
        }
        if (!isNullOrUndefined(footerTable)) {
            footerTable.classList.add('e-tableborder');
        }
        this.refresh();
        this.doubleTapEvent(e);
        this.isDblClk = true;
    }
    getPointX(e) {
        if (e.touches && e.touches.length) {
            return e.touches[0].pageX;
        }
        else {
            return e.pageX;
        }
    }
    refreshColumnWidth() {
        const columns = this.parent.getColumns();
        for (const ele of [].slice.apply(this.parent.getHeaderContent().querySelectorAll('th.e-headercell:not(.e-hide):not([style*="display: none"])'))) {
            for (const column of columns) {
                if (ele.querySelector('[data-mappinguid]') &&
                    ele.querySelector('[data-mappinguid]').getAttribute('data-mappinguid') === column.uid && column.visible) {
                    column.width = ele.getBoundingClientRect().width;
                    break;
                }
            }
        }
        return columns;
    }
    refreshStackedColumnWidth() {
        for (const stackedColumn of this.parent.getStackedColumns(this.parent.columns)) {
            stackedColumn.width = this.getStackedWidth(stackedColumn, 0);
        }
    }
    getStackedWidth(column, width) {
        for (const col of column.columns) {
            if (col.visible !== false) {
                if (col.columns) {
                    width = this.getStackedWidth(col, width);
                }
                else {
                    width += parseFloat(col.width.toString());
                }
            }
        }
        return width;
    }
    getTargetColumn(e) {
        let cell = closest(e.target, resizeClassList.header);
        cell = cell.querySelector('.e-headercelldiv') || cell.querySelector('.e-stackedheadercelldiv');
        const uid = cell.getAttribute('data-mappinguid');
        return this.parent.getColumnByUid(uid);
    }
    updateCursor(action) {
        const headerRows = [].slice.call(this.parent.getHeaderContent().querySelectorAll('th'));
        headerRows.push(this.parent.element);
        for (const row of headerRows) {
            row.classList[`${action}`](resizeClassList.cursor);
        }
    }
    refresh() {
        this.column = null;
        this.pageX = null;
        this.element = null;
        this.helper = null;
    }
    appendHelper() {
        this.helper = this.parent.createElement('div', {
            className: resizeClassList.helper
        });
        this.parent.element.appendChild(this.helper);
        this.setHelperHeight();
    }
    setHelperHeight() {
        let height = this.parent.getContent().offsetHeight - this.getScrollBarWidth();
        const rect = closest(this.element, resizeClassList.header);
        const tr = [].slice.call(this.parent.getHeaderContent().querySelectorAll('tr'));
        for (let i = tr.indexOf(rect.parentElement); i < tr.length && i > -1; i++) {
            height += tr[parseInt(i.toString(), 10)].offsetHeight;
        }
        const pos = this.calcPos(rect);
        pos.left += (this.parent.enableRtl ? 0 - 1 : rect.offsetWidth - 2);
        this.helper.style.cssText = 'height: ' + height + 'px; top: ' + pos.top + 'px; left:' + Math.floor(pos.left) + 'px;';
        if (this.parent.enableVirtualization) {
            this.helper.classList.add('e-virtual-rhandler');
        }
    }
    getScrollBarWidth(height) {
        const ele = this.parent.getContent().firstChild;
        return (ele.scrollHeight > ele.clientHeight && height) ||
            ele.scrollWidth > ele.clientWidth ? getScrollBarWidth() : 0;
    }
    removeHelper(e) {
        const cls = e.target.classList;
        if (!(cls.contains(resizeClassList.root) || cls.contains(resizeClassList.icon)) && this.helper) {
            EventHandler.remove(document, Browser.touchStartEvent, this.removeHelper);
            EventHandler.remove(this.helper, Browser.touchStartEvent, this.resizeStart);
            detach(this.helper);
            this.refresh();
        }
    }
    updateHelper() {
        const rect = closest(this.element, resizeClassList.header);
        let left;
        left = Math.floor(this.calcPos(rect).left + (this.parent.enableRtl ? 0 - 1 : rect.offsetWidth - 2));
        const borderWidth = 2; // to maintain the helper inside of grid element.
        if (left > this.parentElementWidth) {
            left = this.parentElementWidth - borderWidth;
        }
        this.helper.style.left = left + 'px';
    }
    calcPos(elem) {
        let parentOffset = {
            top: 0,
            left: 0
        };
        const offset = elem.getBoundingClientRect();
        const doc = elem.ownerDocument;
        let offsetParent = parentsUntil(elem, 'e-grid') || doc.documentElement;
        while (offsetParent &&
            (offsetParent === doc.body || offsetParent === doc.documentElement) &&
            offsetParent.style.position === 'static') {
            offsetParent = offsetParent.parentNode;
        }
        if (offsetParent && offsetParent !== elem && offsetParent.nodeType === 1) {
            parentOffset = offsetParent.getBoundingClientRect();
        }
        return {
            top: offset.top - parentOffset.top,
            left: offset.left - parentOffset.left
        };
    }
    doubleTapEvent(e) {
        if (this.getUserAgent() && this.isDblClk) {
            if (!this.tapped) {
                this.tapped = setTimeout(() => {
                    this.tapped = null;
                }, 300);
            }
            else {
                clearTimeout(this.tapped);
                this.callAutoFit(e);
                this.tapped = null;
            }
        }
    }
    getUserAgent() {
        const userAgent = Browser.userAgent.toLowerCase();
        return /iphone|ipod|ipad/.test(userAgent);
    }
    timeoutHandler() {
        this.tapped = null;
    }
}
